/**
 * Window control (custom-titlebar migration)
 *
 * The app draws its own titlebar (src/components/TitleBar.tsx) with the theme
 * toggle + minimize/maximize/close + a drag region. Under BOTH local-desktop
 * shells the window itself is frameless, so the titlebar must drive the real OS
 * window:
 *
 *  - CEF host → the native GTK toplevel via the `window.cefQuery`
 *               {type:"window",action:...} bridge (host/src/osr.h +
 *               main.cc WindowControl table).
 *
 * The implementation satisfies the `WindowControl` interface so TitleBar is
 * shell-agnostic — it just calls `backend.window.*`.
 */

export interface WindowControl {
  /** Minimize / iconify the window. */
  minimize(): Promise<void>;
  /** Toggle maximize/restore; resolves with the NEW maximized state. */
  toggleMaximize(): Promise<boolean>;
  /** Whether the window is currently maximized. */
  isMaximized(): Promise<boolean>;
  /** Close the window (quits the app, like the OS close button). */
  close(): Promise<void>;
  /**
   * Begin an interactive window move from the current pointer position, so
   * dragging the titlebar moves the window. Under Tauri the `data-tauri-drag-
   * region` attribute handles this natively, but it's harmless to expose here
   * for parity; under CEF this is the ONLY way to make the drag region work.
   */
  startDrag(): Promise<void>;
}

type HostQuery = (payload: Record<string, unknown>) => Promise<string>;

/** CEF implementation — routes through the host's cefQuery window bridge. */
export function createCefWindowControl(hostQuery: HostQuery): WindowControl {
  return {
    async minimize() {
      await hostQuery({ type: 'window', action: 'minimize' });
    },
    async toggleMaximize() {
      const res = await hostQuery({ type: 'window', action: 'toggle-maximize' });
      return res === 'true';
    },
    async isMaximized() {
      const res = await hostQuery({ type: 'window', action: 'is-maximized' });
      return res === 'true';
    },
    async close() {
      await hostQuery({ type: 'window', action: 'close' });
    },
    async startDrag() {
      await hostQuery({ type: 'window', action: 'start-drag' });
    },
  };
}
